/**
 * Notification helper functions
 */

/**
 * Get detailed description for notification types
 * @param id - The notification type ID
 * @returns Detailed description string
 */
export const getNotificationDescription = (id: string): string => {
  switch (id) {
    case 'smart_notifications':
      return 'AI-powered market insights. Pro users only.';
    case 'daily_alerts':
      return 'Get notified if the price changes significantly in a single day.';
    case 'weekly_alerts':
      return 'Alert for major movements during the week.';
    case 'news_alerts':
      return 'Get notified when relevant news is published that affects a stock.';
    case 'insider_sales':
      return 'Be alerted when executives or insiders sell large amounts of stock.';
    case 'rsi_alerts':
      return 'Receive alerts when the RSI crosses key thresholds.';
    case 'sma_alerts':
      return 'Get notified when price crosses moving averages.';
    case 'insight_alerts':
      return 'Alerts for key insights from earnings calls.';
    case 'earnings_alerts':
      return 'Get notified about new earnings transcripts.';
    default:
      return '';
  }
};

/**
 * Get user-friendly notification type names
 * @param id - The notification type ID
 * @returns User-friendly name
 */
export const getNotificationDisplayName = (id: string): string => {
  switch (id) {
    case 'smart_notifications':
      return 'Smart Summary';
    case 'daily_alerts':
      return 'Daily Price Changes';
    case 'weekly_alerts':
      return 'Weekly Price Changes';
    case 'news_alerts':
      return 'News Alerts';
    case 'insider_sales':
      return 'Insider Trading';
    case 'rsi_alerts':
      return 'RSI Indicators';
    case 'sma_alerts':
      return 'Moving Averages';
    case 'insight_alerts':
      return 'Earnings Insights';
    case 'earnings_alerts':
      return 'Earnings Transcripts';
    default:
      return id;
  }
};

/**
 * Validate notification preferences array
 * @param preferences - Array of notification preferences
 * @returns boolean indicating if preferences are valid
 */
export const validateNotificationPreferences = (preferences: unknown): boolean => {
  if (!Array.isArray(preferences)) return false;

  return preferences.every(
    (pref) =>
      typeof pref === 'object' &&
      pref !== null &&
      'id' in pref &&
      'description' in pref &&
      'enabled' in pref &&
      typeof pref.id === 'string' &&
      typeof pref.description === 'string' &&
      typeof pref.enabled === 'boolean',
  );
};
